import React from 'react';
import { CheckCircle, Plus } from 'lucide-react';
import { AvailableDancerCardProps } from '../../types/dancers';

/**
 * Card component for displaying an available dancer
 */
const AvailableDancerCard: React.FC<AvailableDancerCardProps> = ({
  dancer,
  isSelected,
  toggleDancerSelection,
  calculateAge,
  getInitials,
  isDarkMode
}) => {
  const age = calculateAge(dancer.dateOfBirth || dancer.birthDate || '');

  return (
    <div
      className={`p-3 rounded-lg border transition-all duration-200 hover:shadow-md ${
        isSelected
          ? isDarkMode
            ? "border-blue-800 bg-blue-900/20"
            : "border-blue-300 bg-blue-50"
          : isDarkMode
          ? "border-gray-700 hover:border-gray-600"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {/* Profile Photo or Initials */}
          {dancer.profilePicture ? (
            <img
              src={dancer.profilePicture}
              alt={`${dancer.firstName} ${dancer.lastName}`}
              className="w-10 h-10 rounded-full object-cover mr-3 border-2 border-gray-200 dark:border-gray-600"
            />
          ) : (
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                isDarkMode
                  ? "bg-gray-700 text-gray-300"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {getInitials(dancer.firstName || '', dancer.lastName || '')}
            </div>
          )}
          <div>
            <h4
              className={`font-medium ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {dancer.firstName} {dancer.lastName}
            </h4>
            <p
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Age: {age}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            try {
              const result = toggleDancerSelection(dancer);
              // Only handle as promise if it actually returns a promise
              if (result && typeof result === 'object' && 'then' in result) {
                result.catch((error: Error) => {
                  console.error("Error toggling dancer selection:", error);
                });
              }
            } catch (error) {
              console.error("Error calling toggleDancerSelection:", error);
            }
          }}
          className={`p-2 rounded-full transition-colors ${
            isSelected
              ? isDarkMode
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "bg-blue-500 text-white hover:bg-blue-600"
              : isDarkMode
              ? "bg-gray-700 text-gray-400 hover:bg-gray-600"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
          aria-label={isSelected ? "Remove dancer" : "Add dancer"}
        >
          {isSelected ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <Plus className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export default AvailableDancerCard; 